import React, { useState, useMemo } from 'react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  ScatterChart, Scatter, Line
} from 'recharts';
import { AlertTriangle, TrendingUp, BarChart2, GitBranch, Percent, Shield, Layers, Info, HelpCircle } from 'lucide-react';
import * as stats from '../../../utils/statisticsLib';
import './StatisticalTests.css';

const testRequirements = {
  independentT: {
    description: 'Compare responses to two different questions',
    helpNote: 'This compares how respondents answered two different questions. For example: "Did people rate Question A higher than Question B?"',
    variables: [
      { key: 'group1', label: 'First Question to Compare', type: 'numeric', required: true },
      { key: 'group2', label: 'Second Question to Compare', type: 'numeric', required: true }
    ]
  },
  pairedT: {
    description: 'Compare before/after or pre/post measurements',
    helpNote: 'This compares paired measurements from the same respondents, such as pre-test vs post-test scores.',
    variables: [
      { key: 'before', label: 'Before/Pre-Test Question', type: 'numeric', required: true },
      { key: 'after', label: 'After/Post-Test Question', type: 'numeric', required: true }
    ]
  },
  oneWayAnova: {
    description: 'Compare responses across 3+ questions',
    helpNote: 'This compares average responses across multiple questions. For example: "Are there differences in how people rated these 3 product features?"',
    variables: [
      { key: 'groups', label: 'Questions to Compare (select 3+)', type: 'numeric', required: true, multi: true, min: 3 }
    ]
  },
  kruskalWallis: {
    description: 'Non-parametric comparison across 3+ questions',
    helpNote: 'Similar to ANOVA but for ordinal/ranked data. Compares responses across multiple questions.',
    variables: [
      { key: 'groups', label: 'Questions to Compare (select 3+)', type: 'numeric', required: true, multi: true, min: 3 }
    ]
  },
  chiSquare: {
    description: 'Tests association between two categorical questions',
    helpNote: 'Tests whether responses to one question are related to responses to another. For example: "Is product preference related to age group?"',
    variables: [
      { key: 'cat1', label: 'First Categorical Question', type: 'categorical', required: true },
      { key: 'cat2', label: 'Second Categorical Question', type: 'categorical', required: true }
    ]
  },
  mannWhitney: {
    description: 'Non-parametric comparison of two questions',
    helpNote: 'Similar to t-test but for ordinal/ranked data. Compares responses to two different questions.',
    variables: [
      { key: 'group1', label: 'First Question to Compare', type: 'numeric', required: true },
      { key: 'group2', label: 'Second Question to Compare', type: 'numeric', required: true }
    ]
  },
  wilcoxon: {
    description: 'Non-parametric before/after comparison',
    helpNote: 'Similar to paired t-test but for ordinal data. Compares paired measurements from the same respondents.',
    variables: [
      { key: 'before', label: 'Before/Pre-Test Question', type: 'numeric', required: true },
      { key: 'after', label: 'After/Post-Test Question', type: 'numeric', required: true }
    ]
  },
  pearson: {
    description: 'Measures relationship strength between two questions',
    helpNote: 'Tests whether responses to two questions move together. For example: "Do people who rate satisfaction high also rate loyalty high?"',
    variables: [
      { key: 'var1', label: 'First Question', type: 'numeric', required: true },
      { key: 'var2', label: 'Second Question', type: 'numeric', required: true }
    ]
  },
  spearman: {
    description: 'Rank correlation for ordinal data',
    helpNote: 'Similar to Pearson but for ranked/ordinal data. Tests whether responses to two questions are related.',
    variables: [
      { key: 'var1', label: 'First Question', type: 'numeric', required: true },
      { key: 'var2', label: 'Second Question', type: 'numeric', required: true }
    ]
  },
  pointBiserial: {
    description: 'Correlation between Yes/No and numeric question',
    helpNote: 'Tests relationship between a binary (Yes/No) question and a numeric question.',
    variables: [
      { key: 'var1', label: 'Numeric Question', type: 'numeric', required: true },
      { key: 'var2', label: 'Binary (Yes/No) Question', type: 'binary', required: true }
    ]
  },
  simpleRegression: {
    description: 'Predicts one question from another',
    helpNote: 'Tests whether responses to one question can predict responses to another. For example: "Can satisfaction predict likelihood to recommend?"',
    variables: [
      { key: 'predictor', label: 'Predictor Question (X)', type: 'numeric', required: true },
      { key: 'outcome', label: 'Outcome Question (Y)', type: 'numeric', required: true }
    ]
  },
  cronbach: {
    description: 'Tests scale reliability',
    helpNote: 'Measures whether multiple questions consistently measure the same underlying concept. Use for validating questionnaire scales.',
    variables: [
      { key: 'items', label: 'Scale Items (select 2+)', type: 'numeric', required: true, multi: true, min: 2 }
    ]
  },
  oneProportion: {
    description: 'Tests if observed proportion matches expected',
    helpNote: 'Tests whether the proportion of responses differs from an expected value.',
    variables: [
      { key: 'cat1', label: 'Categorical Question', type: 'categorical', required: true }
    ]
  },
  twoProportion: {
    description: 'Compares proportions from two questions',
    helpNote: 'Tests whether the proportion of a response differs between two questions.',
    variables: [
      { key: 'cat1', label: 'First Question', type: 'categorical', required: true },
      { key: 'cat2', label: 'Second Question', type: 'categorical', required: true }
    ]
  },
  fisherExact: {
    description: 'Exact test for small sample associations',
    helpNote: 'Like Chi-Square but more accurate for small samples. Tests association between two categorical questions.',
    variables: [
      { key: 'cat1', label: 'First Categorical Question', type: 'categorical', required: true },
      { key: 'cat2', label: 'Second Categorical Question', type: 'categorical', required: true }
    ]
  },
  mcnemar: {
    description: 'Tests change in paired binary responses',
    helpNote: 'Tests whether responses changed between two time points for binary (Yes/No) questions.',
    variables: [
      { key: 'before', label: 'Before Question (Binary)', type: 'binary', required: true },
      { key: 'after', label: 'After Question (Binary)', type: 'binary', required: true }
    ]
  }
};

const dataTypeDescriptions = {
  numeric: {
    label: 'Numeric',
    tooltip: 'Includes: Likert scales (1-5, 1-7), Linear scales (1-10), Number inputs. These questions produce numerical values that can be averaged.',
    questionTypes: ['linearScale', 'likertScale', 'number']
  },
  categorical: {
    label: 'Categorical',
    tooltip: 'Includes: Multiple choice, Dropdown, Yes/No, Checkboxes. These questions produce distinct categories or groups.',
    questionTypes: ['multipleChoice', 'dropdown', 'yesNo', 'checkboxes']
  },
  binary: {
    label: 'Binary (Yes/No)',
    tooltip: 'Includes: Yes/No questions, or Multiple choice with exactly 2 options. These questions have only two possible answers.',
    questionTypes: ['yesNo']
  }
};

const testCategories = [
  {
    id: 'comparing',
    label: 'Comparing Groups',
    icon: BarChart2,
    tests: [
      { id: 'chiSquare', label: 'Chi-Square Test of Independence', simplified: false },
      { id: 'independentT', label: 'Independent Samples t-test', simplified: false },
      { id: 'pairedT', label: 'Paired Samples t-test', simplified: false },
      { id: 'oneWayAnova', label: 'One-Way ANOVA', simplified: false },
      { id: 'repeatedAnova', label: 'Repeated Measures ANOVA', simplified: true },
      { id: 'kruskalWallis', label: 'Kruskal-Wallis Test', simplified: false },
      { id: 'mannWhitney', label: 'Mann-Whitney U Test', simplified: false },
      { id: 'wilcoxon', label: 'Wilcoxon Signed-Rank Test', simplified: false }
    ]
  },
  {
    id: 'relationships',
    label: 'Relationships Between Variables',
    icon: GitBranch,
    tests: [
      { id: 'pearson', label: 'Pearson Correlation', simplified: false },
      { id: 'spearman', label: 'Spearman Correlation', simplified: false },
      { id: 'pointBiserial', label: 'Point-Biserial Correlation', simplified: false },
      { id: 'simpleRegression', label: 'Simple Linear Regression', simplified: false },
      { id: 'multipleRegression', label: 'Multiple Linear Regression', simplified: false },
      { id: 'logisticRegression', label: 'Logistic Regression', simplified: true }
    ]
  },
  {
    id: 'proportions',
    label: 'Proportions & Frequencies',
    icon: Percent,
    tests: [
      { id: 'oneProportion', label: 'One-Sample Proportion Test', simplified: false },
      { id: 'twoProportion', label: 'Two-Proportion Z-Test', simplified: false },
      { id: 'mcnemar', label: 'McNemar Test', simplified: false },
      { id: 'fisherExact', label: "Fisher's Exact Test", simplified: false }
    ]
  },
  {
    id: 'reliability',
    label: 'Scale Reliability & Validity',
    icon: Shield,
    tests: [
      { id: 'cronbach', label: "Cronbach's Alpha", simplified: false },
      { id: 'efa', label: 'Factor Analysis (Exploratory)', simplified: true },
      { id: 'cfa', label: 'Confirmatory Factor Analysis', simplified: true }
    ]
  },
  {
    id: 'advanced',
    label: 'Advanced Tests',
    icon: Layers,
    tests: [
      { id: 'ancova', label: 'ANCOVA', simplified: true },
      { id: 'manova', label: 'MANOVA', simplified: true },
      { id: 'twoWayAnova', label: 'Two-Way ANOVA', simplified: false },
      { id: 'mixedAnova', label: 'Mixed-Design ANOVA', simplified: true }
    ]
  }
];

const StatisticalTests = ({ survey }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedVariables, setSelectedVariables] = useState({});
  const [testResult, setTestResult] = useState(null);

  const responses = survey.responses || [];
  const questions = survey.questions || [];

  const numericQuestions = useMemo(() => 
    questions.filter(q => ['linearScale', 'likertScale', 'number'].includes(q.type)),
    [questions]
  );

  const categoricalQuestions = useMemo(() =>
    questions.filter(q => ['multipleChoice', 'dropdown', 'yesNo', 'checkboxes'].includes(q.type)),
    [questions]
  );

  const binaryQuestions = useMemo(() =>
    questions.filter(q => {
      if (q.type === 'yesNo') return true;
      if (['multipleChoice', 'dropdown', 'checkboxes'].includes(q.type)) {
        const options = q.options || [];
        return options.length === 2;
      }
      return false;
    }),
    [questions]
  );

  const getQuestionsForType = (type) => {
    switch (type) {
      case 'numeric': return numericQuestions;
      case 'categorical': return categoricalQuestions;
      case 'binary': return binaryQuestions;
      default: return questions;
    }
  };

  const checkTestDataAvailability = (testId) => {
    const requirements = testRequirements[testId];
    if (!requirements) return { available: true, missing: [] };
    
    const missing = [];
    for (const varReq of requirements.variables) {
      const availableQuestions = getQuestionsForType(varReq.type);
      const minRequired = varReq.multi ? (varReq.min || 2) : 1;
      if (availableQuestions.length < minRequired) {
        missing.push({
          type: varReq.type,
          label: dataTypeDescriptions[varReq.type]?.label || varReq.type,
          needed: minRequired,
          available: availableQuestions.length
        });
      }
    }
    
    return { available: missing.length === 0, missing };
  };

  const getQuestionData = (questionId) => {
    return responses.map(r => r.answers?.[questionId]).filter(a => a !== undefined && a !== null);
  };

  const getOptionLabel = (questionId, optionId) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return optionId;
    
    if (question.type === 'yesNo') {
      return optionId === 'yes' ? 'Yes' : optionId === 'no' ? 'No' : optionId;
    }
    
    const options = question.options || question.settings?.options || [];
    const option = options.find(o => o.id === optionId);
    return option?.text || optionId;
  };

  const getQuestionLabel = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    return question?.text?.slice(0, 60) || 'Variable';
  };

  const buildHypotheses = (testId, variables) => {
    let null_h = '';
    let alt_h = '';
    let var1Label = '';
    let var2Label = '';
    
    switch (testId) {
      case 'chiSquare':
        var1Label = getQuestionLabel(variables.cat1);
        var2Label = getQuestionLabel(variables.cat2);
        null_h = `H₀: There is no association between "${var1Label}" and "${var2Label}" in the population. The two variables are independent.`;
        alt_h = `H₁: There is an association between "${var1Label}" and "${var2Label}" in the population. The two variables are not independent.`;
        break;
      case 'independentT':
      case 'mannWhitney':
        var1Label = getQuestionLabel(variables.group1);
        var2Label = getQuestionLabel(variables.group2);
        null_h = `H₀: There is no significant difference between the population means (or distributions) of "${var1Label}" and "${var2Label}".`;
        alt_h = `H₁: There is a significant difference between the population means (or distributions) of "${var1Label}" and "${var2Label}".`;
        break;
      case 'pairedT':
      case 'wilcoxon':
        var1Label = getQuestionLabel(variables.before);
        var2Label = getQuestionLabel(variables.after);
        null_h = `H₀: There is no significant difference between "${var1Label}" (before) and "${var2Label}" (after). The mean difference equals zero.`;
        alt_h = `H₁: There is a significant difference between "${var1Label}" (before) and "${var2Label}" (after). The mean difference does not equal zero.`;
        break;
      case 'oneWayAnova':
      case 'kruskalWallis':
        const groupLabels = (variables.groups || []).map(id => getQuestionLabel(id)).slice(0, 3).join(', ');
        null_h = `H₀: All group means are equal across the compared variables (${groupLabels}${(variables.groups?.length || 0) > 3 ? ', ...' : ''}).`;
        alt_h = `H₁: At least one group mean differs significantly from the others.`;
        break;
      case 'pearson':
      case 'spearman':
        var1Label = getQuestionLabel(variables.var1);
        var2Label = getQuestionLabel(variables.var2);
        null_h = `H₀: There is no correlation between "${var1Label}" and "${var2Label}" in the population (ρ = 0).`;
        alt_h = `H₁: There is a correlation between "${var1Label}" and "${var2Label}" in the population (ρ ≠ 0).`;
        break;
      case 'simpleRegression':
        var1Label = getQuestionLabel(variables.predictor);
        var2Label = getQuestionLabel(variables.outcome);
        null_h = `H₀: "${var1Label}" does not significantly predict "${var2Label}". The regression slope equals zero (β = 0).`;
        alt_h = `H₁: "${var1Label}" significantly predicts "${var2Label}". The regression slope differs from zero (β ≠ 0).`;
        break;
      case 'cronbach':
        null_h = `This is a reliability assessment, not a hypothesis test. No null hypothesis is applicable.`;
        alt_h = `Internal consistency is assessed using Cronbach's alpha to determine if items measure the same construct.`;
        break;
      case 'oneProportion':
        null_h = `H₀: The population proportion equals the hypothesized value (p = p₀).`;
        alt_h = `H₁: The population proportion differs from the hypothesized value (p ≠ p₀).`;
        break;
      case 'twoProportion':
        null_h = `H₀: The two population proportions are equal (p₁ = p₂).`;
        alt_h = `H₁: The two population proportions are not equal (p₁ ≠ p₂).`;
        break;
      default:
        null_h = `H₀: No significant effect or relationship exists.`;
        alt_h = `H₁: A significant effect or relationship exists.`;
    }
    
    return { null_h, alt_h, var1Label, var2Label };
  };

  const generateExplanation = (testId, result, hypotheses) => {
    if (!result) return '';
    
    const significant = result.p < 0.05;
    const effectSizeWord = (d) => Math.abs(d) < 0.2 ? 'very small' : Math.abs(d) < 0.5 ? 'small' : Math.abs(d) < 0.8 ? 'medium' : 'large';

    let explanation = '';

    switch (testId) {
      case 'independentT':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `The two groups ARE significantly different. Group 1 (average: ${result.mean1?.toFixed(2)}) differs from Group 2 (average: ${result.mean2?.toFixed(2)}) by ${Math.abs(result.mean1 - result.mean2)?.toFixed(2)} points. This difference is unlikely to be due to chance—it represents a real difference between these groups.`
  : `The two groups are NOT significantly different. While Group 1 averaged ${result.mean1?.toFixed(2)} and Group 2 averaged ${result.mean2?.toFixed(2)}, this ${Math.abs(result.mean1 - result.mean2)?.toFixed(2)}-point difference could easily occur by chance. We cannot conclude the groups truly differ.`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: This tells us the probability that we'd see this difference (or larger) if there was actually no real difference. ${result.p < 0.05 ? 'Since p < 0.05, the result is statistically significant.' : 'Since p ≥ 0.05, the result is not statistically significant.'}
• **Effect size (Cohen's d = ${Math.abs(result.cohenD)?.toFixed(2)})**: This measures HOW BIG the difference is in practical terms. Your effect size is "${effectSizeWord(result.cohenD)}"—think of it like this: ${Math.abs(result.cohenD) < 0.2 ? 'the groups are almost identical' : Math.abs(result.cohenD) < 0.5 ? 'there is a noticeable but modest difference' : Math.abs(result.cohenD) < 0.8 ? 'there is a meaningful, moderate difference' : 'there is a substantial, important difference'}.
• **Sample sizes**: Group 1 had ${result.n1} responses, Group 2 had ${result.n2} responses.

**FOR YOUR RESEARCH REPORT:**
If you're writing an academic paper, here's how to report this result using standard APA format:
"t(${result.df?.toFixed(0)}) = ${result.t?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}, d = ${result.cohenD?.toFixed(2)}"
(This compact notation includes: the test statistic, degrees of freedom, probability value, and effect size)`;
        break;

      case 'pairedT':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `There IS a significant change between the two measurements. On average, scores ${result.meanDiff > 0 ? 'increased' : 'decreased'} by ${Math.abs(result.meanDiff)?.toFixed(2)} points. This change is unlikely to be due to random variation—something real happened between the two time points.`
  : `There is NO significant change between the two measurements. The average change of ${result.meanDiff?.toFixed(2)} points could easily occur by chance. We cannot conclude that a real change occurred.`}

**KEY NUMBERS EXPLAINED:**
• **Average change = ${result.meanDiff?.toFixed(2)}**: This is how much scores changed on average between the two measurements.
• **p-value = ${result.p?.toFixed(3)}**: The probability of seeing this change if there was no real effect. ${result.p < 0.05 ? 'Since p < 0.05, the change is statistically significant.' : 'Since p ≥ 0.05, the change is not statistically significant.'}
• **Effect size (Cohen's d = ${Math.abs(result.cohenD)?.toFixed(2)})**: This is a "${effectSizeWord(result.cohenD)}" effect, meaning ${Math.abs(result.cohenD) < 0.2 ? 'the change is barely noticeable' : Math.abs(result.cohenD) < 0.5 ? 'the change is small but detectable' : Math.abs(result.cohenD) < 0.8 ? 'the change is moderate and meaningful' : 'the change is large and important'}.
• **Number of paired observations**: ${result.n}

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "t(${result.df}) = ${result.t?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}, d = ${result.cohenD?.toFixed(2)}"`;
        break;

      case 'oneWayAnova':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `At least one group IS significantly different from the others. The groups do not all have the same average—there are real differences between them. However, this test only tells us that differences exist; you would need follow-up tests to determine exactly which groups differ from which.`
  : `The groups are NOT significantly different. Any differences in their averages could be due to chance. We cannot conclude that the groups truly differ.`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: ${result.p < 0.05 ? 'Since p < 0.05, at least one group is significantly different.' : 'Since p ≥ 0.05, no significant differences were found.'}
• **Effect size (η² = ${result.etaSquared?.toFixed(3)})**: This tells us what percentage of the variation in scores is explained by group membership. Your η² of ${result.etaSquared?.toFixed(3)} means ${(result.etaSquared * 100)?.toFixed(1)}% of score differences are due to being in different groups. This is a ${result.etaSquared < 0.06 ? 'small' : result.etaSquared < 0.14 ? 'medium' : 'large'} effect.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "F(${result.dfBetween}, ${result.dfWithin}) = ${result.F?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}, η² = ${result.etaSquared?.toFixed(2)}"`;
        break;

      case 'pearson':
      case 'spearman':
        const corrStrength = Math.abs(result.r) < 0.1 ? 'negligible' : Math.abs(result.r) < 0.3 ? 'weak' : Math.abs(result.r) < 0.5 ? 'moderate' : Math.abs(result.r) < 0.7 ? 'strong' : 'very strong';
        const corrDirection = result.r > 0 ? 'positive' : 'negative';
        
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `There IS a significant relationship between these two variables. The correlation is ${corrStrength} and ${corrDirection} (r = ${result.r?.toFixed(2)}). ${result.r > 0 ? 'When one variable goes up, the other tends to go up too.' : 'When one variable goes up, the other tends to go down.'}`
  : `There is NO significant relationship between these two variables. The correlation of ${result.r?.toFixed(2)} is not strong enough to rule out chance. We cannot conclude that these variables are truly related.`}

**KEY NUMBERS EXPLAINED:**
• **Correlation (r = ${result.r?.toFixed(2)})**: This ranges from -1 to +1. Zero means no relationship, +1 means perfect positive relationship, -1 means perfect negative relationship. Your value of ${result.r?.toFixed(2)} indicates a ${corrStrength} ${corrDirection} relationship.
• **p-value = ${result.p?.toFixed(3)}**: ${result.p < 0.05 ? 'Since p < 0.05, the correlation is statistically significant.' : 'Since p ≥ 0.05, the correlation is not statistically significant.'}
• **Shared variance (r² = ${(result.r * result.r)?.toFixed(3)})**: This means ${((result.r * result.r) * 100)?.toFixed(1)}% of the variation in one variable can be explained by the other variable.

**IMPORTANT REMINDER:** Correlation does not prove causation! Even a strong correlation doesn't mean one variable causes changes in the other—there could be other explanations.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "r(${result.df}) = ${result.r?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}"`;
        break;

      case 'simpleRegression':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
The predictor variable explains ${(result.rSquared * 100)?.toFixed(1)}% of the variation in the outcome variable. For every 1-unit increase in the predictor, the outcome ${result.slope > 0 ? 'increases' : 'decreases'} by ${Math.abs(result.slope)?.toFixed(3)} units on average.

**KEY NUMBERS EXPLAINED:**
• **Prediction equation**: Predicted Outcome = ${result.intercept?.toFixed(2)} + (${result.slope?.toFixed(3)} × Predictor Value)
• **R² = ${result.rSquared?.toFixed(3)}**: This is the "coefficient of determination"—it tells us how well the predictor explains the outcome. Your R² of ${result.rSquared?.toFixed(3)} means the predictor accounts for ${(result.rSquared * 100)?.toFixed(1)}% of outcome variation. ${result.rSquared < 0.13 ? 'This is a small effect.' : result.rSquared < 0.26 ? 'This is a medium effect.' : 'This is a large effect.'}
• **Slope = ${result.slope?.toFixed(3)}**: For each 1-unit increase in the predictor, the outcome changes by this amount.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "B = ${result.slope?.toFixed(2)}, R² = ${result.rSquared?.toFixed(2)}"`;
        break;

      case 'chiSquare':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `There IS a significant association between these two categorical variables. How people answered one question is related to how they answered the other—the variables are NOT independent of each other.`
  : `There is NO significant association between these two categorical variables. How people answered one question does not appear to be related to how they answered the other—the variables seem to be independent (unrelated).`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: This is the probability of seeing this pattern (or a stronger one) if the variables were truly unrelated. ${result.p < 0.05 ? 'Since p < 0.05, we conclude the variables ARE related.' : 'Since p ≥ 0.05, we cannot conclude the variables are related.'}
• **Effect size (Cramér's V = ${result.cramersV?.toFixed(2)})**: This measures the STRENGTH of the association on a scale from 0 (no association) to 1 (perfect association). Your V of ${result.cramersV?.toFixed(2)} indicates a ${result.cramersV < 0.1 ? 'negligible' : result.cramersV < 0.3 ? 'small' : result.cramersV < 0.5 ? 'medium' : 'large'} association. Think of it like a correlation for categorical variables.
• **Degrees of freedom (df = ${result.df})**: This reflects the size of your contingency table (number of rows minus 1, times number of columns minus 1).
• **Chi-square value (χ² = ${result.chiSquare?.toFixed(2)})**: A larger value indicates a bigger difference between what we observed and what we'd expect if the variables were unrelated.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "χ²(${result.df}) = ${result.chiSquare?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}, V = ${result.cramersV?.toFixed(2)}"
(This notation shows: chi-square symbol, degrees of freedom, chi-square value, probability, and Cramér's V effect size)`;
        break;

      case 'cronbach':
        const alphaQuality = result.alpha < 0.5 ? 'unacceptable' : result.alpha < 0.6 ? 'poor' : result.alpha < 0.7 ? 'questionable' : result.alpha < 0.8 ? 'acceptable' : result.alpha < 0.9 ? 'good' : 'excellent';
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
Your ${result.k} items have "${alphaQuality}" internal consistency (α = ${result.alpha?.toFixed(2)}). ${result.alpha >= 0.7 
  ? 'This means the items work well together as a scale—people who score high on one item tend to score high on the others. You can confidently combine these items into a single score.'
  : 'This suggests the items may not be measuring the same thing consistently. Consider reviewing individual items or whether they truly belong together as a scale.'}

**KEY NUMBERS EXPLAINED:**
• **Cronbach's Alpha (α = ${result.alpha?.toFixed(2)})**: This measures how consistently your items measure the same thing. It ranges from 0 to 1:
  - 0.9+ = Excellent reliability
  - 0.8-0.9 = Good reliability  
  - 0.7-0.8 = Acceptable reliability
  - 0.6-0.7 = Questionable reliability
  - Below 0.6 = Poor reliability
• **Number of items**: ${result.k}

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "Internal consistency was ${alphaQuality} (α = ${result.alpha?.toFixed(2)})"`;
        break;

      case 'mannWhitney':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `The two groups ARE significantly different. One group tends to have higher values than the other—this difference is unlikely to be due to chance.`
  : `The two groups are NOT significantly different. Any differences in their rankings could easily occur by chance.`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: ${result.p < 0.05 ? 'Since p < 0.05, the groups are significantly different.' : 'Since p ≥ 0.05, the groups are not significantly different.'}
• **U statistic = ${result.U?.toFixed(0)}**: This measures how much one group's values exceed the other's when comparing all possible pairs.
• **z-score = ${result.z?.toFixed(2)}**: This standardized score tells us how unusual our result is. Values beyond ±1.96 are significant at the 0.05 level.
• **Sample sizes**: Group 1 had ${result.n1} responses, Group 2 had ${result.n2} responses.

**WHEN TO USE THIS TEST:** This is an alternative to the t-test when your data isn't normally distributed or when you have ordinal (ranked) data.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "U = ${result.U?.toFixed(0)}, z = ${result.z?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}"`;
        break;

      case 'wilcoxon':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `There IS a significant change between the two measurements. Scores systematically shifted in one direction—this is unlikely to be due to random variation.`
  : `There is NO significant change between the two measurements. Any changes could easily be due to random variation.`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: ${result.p < 0.05 ? 'Since p < 0.05, the change is significant.' : 'Since p ≥ 0.05, the change is not significant.'}
• **W statistic = ${result.W?.toFixed(0)}**: This is based on ranking the size of changes and summing the ranks.
• **z-score = ${result.z?.toFixed(2)}**: A standardized score; values beyond ±1.96 are significant.
• **Number of pairs**: ${result.n}

**WHEN TO USE THIS TEST:** This is an alternative to the paired t-test when your data isn't normally distributed or when you have ordinal (ranked) data.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "W = ${result.W?.toFixed(0)}, z = ${result.z?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}"`;
        break;

      case 'kruskalWallis':
        explanation = `**WHAT THIS MEANS IN PLAIN LANGUAGE:**
${significant 
  ? `At least one group IS significantly different from the others. The groups don't all come from the same distribution—there are real differences. You would need follow-up tests to determine exactly which groups differ.`
  : `The groups are NOT significantly different. Any differences could be due to chance.`}

**KEY NUMBERS EXPLAINED:**
• **p-value = ${result.p?.toFixed(3)}**: ${result.p < 0.05 ? 'Since p < 0.05, at least one group is different.' : 'Since p ≥ 0.05, no significant differences were found.'}
• **H statistic = ${result.H?.toFixed(2)}**: This measures the extent to which groups differ in their rankings. Larger values suggest bigger differences.
• **Degrees of freedom (df = ${result.df})**: This equals the number of groups minus 1.

**WHEN TO USE THIS TEST:** This is an alternative to one-way ANOVA when your data isn't normally distributed or when you have ordinal (ranked) data.

**FOR YOUR RESEARCH REPORT:**
Standard APA format: "H(${result.df}) = ${result.H?.toFixed(2)}, p ${result.p < 0.001 ? '< .001' : `= ${result.p?.toFixed(3)}`}"`;
        break;

      default:
        explanation = `**INTERPRETING YOUR RESULTS:**
• Compare the p-value to 0.05: If p < 0.05, the result is statistically significant.
• Look at the effect size to understand practical importance (how big the effect is in real-world terms).
• Consider your sample size—small samples may miss real effects, while very large samples can find tiny, unimportant effects.

**FOR YOUR RESEARCH REPORT:**
Include the test statistic, degrees of freedom, p-value, and effect size when available.`;
    }

    return explanation;
  };

  const runTest = () => {
    if (!selectedTest) return;

    let result = null;

    try {
      switch (selectedTest) {
        case 'independentT': {
          const data1 = getQuestionData(selectedVariables.group1).map(Number).filter(n => !isNaN(n));
          const data2 = getQuestionData(selectedVariables.group2).map(Number).filter(n => !isNaN(n));
          result = stats.independentTTest(data1, data2);
          break;
        }
        case 'pairedT': {
          const before = getQuestionData(selectedVariables.before).map(Number).filter(n => !isNaN(n));
          const after = getQuestionData(selectedVariables.after).map(Number).filter(n => !isNaN(n));
          result = stats.pairedTTest(before, after);
          break;
        }
        case 'oneWayAnova': {
          const groups = (selectedVariables.groups || []).map(qId => 
            getQuestionData(qId).map(Number).filter(n => !isNaN(n))
          ).filter(g => g.length > 0);
          result = stats.oneWayAnova(groups);
          break;
        }
        case 'pearson':
        case 'spearman': {
          const x = getQuestionData(selectedVariables.var1).map(Number).filter(n => !isNaN(n));
          const y = getQuestionData(selectedVariables.var2).map(Number).filter(n => !isNaN(n));
          const minLen = Math.min(x.length, y.length);
          result = selectedTest === 'pearson' 
            ? stats.pearsonCorrelation(x.slice(0, minLen), y.slice(0, minLen))
            : stats.spearmanCorrelation(x.slice(0, minLen), y.slice(0, minLen));
          if (result) {
            result.scatterData = x.slice(0, minLen).map((xi, i) => ({ x: xi, y: y[i] }));
          }
          break;
        }
        case 'simpleRegression': {
          const xData = getQuestionData(selectedVariables.predictor).map(Number).filter(n => !isNaN(n));
          const yData = getQuestionData(selectedVariables.outcome).map(Number).filter(n => !isNaN(n));
          const minLen = Math.min(xData.length, yData.length);
          result = stats.simpleLinearRegression(xData.slice(0, minLen), yData.slice(0, minLen));
          if (result) {
            result.scatterData = xData.slice(0, minLen).map((xi, i) => ({ x: xi, y: yData[i] }));
            result.lineData = [
              { x: Math.min(...xData), y: result.slope * Math.min(...xData) + result.intercept },
              { x: Math.max(...xData), y: result.slope * Math.max(...xData) + result.intercept }
            ];
          }
          break;
        }
        case 'cronbach': {
          const items = (selectedVariables.items || []).map(qId =>
            getQuestionData(qId).map(Number).filter(n => !isNaN(n))
          );
          result = stats.cronbachAlpha(items);
          break;
        }
        case 'chiSquare': {
          const var1Data = getQuestionData(selectedVariables.cat1);
          const var2Data = getQuestionData(selectedVariables.cat2);
          const categories1 = [...new Set(var1Data)];
          const categories2 = [...new Set(var2Data)];
          const table = categories1.map(c1 => 
            categories2.map(c2 => 
              var1Data.filter((v, i) => v === c1 && var2Data[i] === c2).length
            )
          );
          result = stats.chiSquareIndependence(table);
          result.table = table;
          result.rowLabels = categories1.map(id => getOptionLabel(selectedVariables.cat1, id));
          result.colLabels = categories2.map(id => getOptionLabel(selectedVariables.cat2, id));
          break;
        }
        case 'mannWhitney': {
          const g1 = getQuestionData(selectedVariables.group1).map(Number).filter(n => !isNaN(n));
          const g2 = getQuestionData(selectedVariables.group2).map(Number).filter(n => !isNaN(n));
          result = stats.mannWhitneyU(g1, g2);
          break;
        }
        case 'wilcoxon': {
          const before = getQuestionData(selectedVariables.before).map(Number).filter(n => !isNaN(n));
          const after = getQuestionData(selectedVariables.after).map(Number).filter(n => !isNaN(n));
          result = stats.wilcoxonSignedRank(before, after);
          break;
        }
        case 'kruskalWallis': {
          const groups = (selectedVariables.groups || []).map(qId =>
            getQuestionData(qId).map(Number).filter(n => !isNaN(n))
          ).filter(g => g.length > 0);
          result = stats.kruskalWallis(groups);
          break;
        }
        default:
          result = { message: 'Test implementation pending' };
      }
    } catch (error) {
      result = { error: error.message };
    }

    if (result && !result.error) {
      const hypotheses = buildHypotheses(selectedTest, selectedVariables);
      result.hypotheses = hypotheses;
      result.explanation = generateExplanation(selectedTest, result, hypotheses);
    }

    setTestResult(result);
  };

  const renderVariableSelector = () => {
    if (!selectedTest) return null;

    const requirements = testRequirements[selectedTest];
    const availability = checkTestDataAvailability(selectedTest);
    
    if (!requirements) {
      return (
        <div className="variable-selector">
          <p className="info-text">Variable selection for this test is under development.</p>
        </div>
      );
    }

    if (!availability.available) {
      const missingTypes = availability.missing.map(m => m.label).join(', ');
      return (
        <div className="data-unavailable-warning">
          <AlertTriangle size={20} />
          <div className="warning-content">
            <strong>Required data not available</strong>
            <p>This test requires <strong>{missingTypes}</strong> data, but your survey doesn't have enough questions of this type.</p>
            <div className="data-type-help">
              {availability.missing.map(m => (
                <div key={m.type} className="type-explanation">
                  <strong>{m.label}</strong>: {dataTypeDescriptions[m.type]?.tooltip}
                  <br />
                  <span className="requirement-status">You need at least {m.needed} question(s), but have {m.available}.</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    const selectors = [];
    
    if (requirements.helpNote) {
      selectors.push(
        <div key="helpNote" className="test-help-note">
          <Info size={16} />
          <span>{requirements.helpNote}</span>
        </div>
      );
    }

    requirements.variables.forEach(varReq => {
      const availableQuestions = getQuestionsForType(varReq.type);
      const typeInfo = dataTypeDescriptions[varReq.type];
      
      if (varReq.multi) {
        selectors.push(
          <div key={varReq.key} className="variable-selector">
            <label>
              {varReq.label}
              <span className="data-type-badge" title={typeInfo?.tooltip}>
                {typeInfo?.label}
                <HelpCircle size={14} className="help-icon" />
              </span>
            </label>
            {availableQuestions.length === 0 ? (
              <div className="no-options-message">
                No {typeInfo?.label.toLowerCase()} questions available in your survey.
              </div>
            ) : (
              <div className="multi-select">
                {availableQuestions.map(q => (
                  <label key={q.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={(selectedVariables[varReq.key] || []).includes(q.id)}
                      onChange={e => {
                        const current = selectedVariables[varReq.key] || [];
                        setSelectedVariables(v => ({
                          ...v,
                          [varReq.key]: e.target.checked
                            ? [...current, q.id]
                            : current.filter(id => id !== q.id)
                        }));
                      }}
                    />
                    {q.text.substring(0, 40)}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      } else {
        selectors.push(
          <div key={varReq.key} className="variable-selector">
            <label>
              {varReq.label}
              <span className="data-type-badge" title={typeInfo?.tooltip}>
                {typeInfo?.label}
                <HelpCircle size={14} className="help-icon" />
              </span>
            </label>
            {availableQuestions.length === 0 ? (
              <div className="no-options-message">
                No {typeInfo?.label.toLowerCase()} questions available in your survey.
              </div>
            ) : (
              <select 
                value={selectedVariables[varReq.key] || ''} 
                onChange={e => setSelectedVariables(v => ({ ...v, [varReq.key]: e.target.value }))}
              >
                <option value="">Select variable...</option>
                {availableQuestions.map(q => (
                  <option key={q.id} value={q.id}>{q.text.substring(0, 50)}</option>
                ))}
              </select>
            )}
          </div>
        );
      }
    });

    return selectors;
  };

  const renderResults = () => {
    if (!testResult) return null;

    if (testResult.error) {
      return <div className="test-error">Error: {testResult.error}</div>;
    }

    return (
      <div className="test-results">
        {testResult.hypotheses && (
          <div className="hypotheses-panel">
            <h4>Research Hypotheses</h4>
            <div className="hypothesis-box null-hypothesis">
              <strong>Null Hypothesis (H₀):</strong>
              <p>{testResult.hypotheses.null_h}</p>
            </div>
            <div className="hypothesis-box alt-hypothesis">
              <strong>Alternative Hypothesis (H₁):</strong>
              <p>{testResult.hypotheses.alt_h}</p>
            </div>
          </div>
        )}

        <h4>Statistical Results</h4>
        
        <div className="results-table-wrapper">
          <table className="results-table">
            <thead>
              <tr>
                <th>Statistic</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(testResult)
                .filter(([key]) => !['explanation', 'scatterData', 'lineData', 'table', 'rowLabels', 'colLabels', 'hypotheses'].includes(key))
                .map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{typeof value === 'number' ? value.toFixed(4) : String(value)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {testResult.scatterData && (
          <div className="chart-wrapper">
            <h5>Scatter Plot</h5>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" type="number" name="X" />
                <YAxis dataKey="y" type="number" name="Y" />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter data={testResult.scatterData} fill="#6366f1" />
                {testResult.lineData && (
                  <Line data={testResult.lineData} dataKey="y" stroke="#dc2626" dot={false} />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        )}

        {testResult.table && (
          <div className="contingency-table-wrapper">
            <h5>Contingency Table</h5>
            <table className="contingency-table">
              <thead>
                <tr>
                  <th></th>
                  {testResult.colLabels.map((label, i) => (
                    <th key={i}>{String(label).substring(0, 15)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {testResult.table.map((row, i) => (
                  <tr key={i}>
                    <td className="row-label">{String(testResult.rowLabels[i]).substring(0, 15)}</td>
                    {row.map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {testResult.explanation && (
          <div className="results-explanation">
            <h5><Info size={18} /> Interpretation</h5>
            <div 
              className="explanation-content"
              dangerouslySetInnerHTML={{ 
                __html: testResult.explanation
                  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>')
                  .replace(/• /g, '<span class="bullet">•</span> ')
              }} 
            />
          </div>
        )}
      </div>
    );
  };

  if (responses.length === 0) {
    return (
      <div className="stats-empty">
        <TrendingUp size={48} />
        <h3>No Data Available</h3>
        <p>Collect survey responses to run statistical analyses.</p>
      </div>
    );
  }

  return (
    <div className="statistical-tests">
      <div className="stats-sidebar">
        <h3>Select Analysis</h3>
        {testCategories.map(category => {
          const Icon = category.icon;
          return (
            <div key={category.id} className="test-category">
              <button 
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
              >
                <Icon size={18} />
                {category.label}
              </button>
              {selectedCategory === category.id && (
                <div className="category-tests">
                  {category.tests.map(test => (
                    <button
                      key={test.id}
                      className={`test-btn ${selectedTest === test.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedTest(test.id);
                        setSelectedVariables({});
                        setTestResult(null);
                      }}
                    >
                      {test.label}
                      {test.simplified && (
                        <span className="simplified-badge" title="Simplified implementation for educational purposes">
                          <AlertTriangle size={12} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="stats-main">
        {selectedTest ? (
          <>
            <div className="test-header">
              <h3>{testCategories.flatMap(c => c.tests).find(t => t.id === selectedTest)?.label}</h3>
              {testCategories.flatMap(c => c.tests).find(t => t.id === selectedTest)?.simplified && (
                <div className="simplified-warning">
                  <AlertTriangle size={16} />
                  This is a simplified implementation suitable for educational/research planning purposes.
                </div>
              )}
            </div>

            <div className="variable-selectors">
              {renderVariableSelector()}
            </div>

            <button className="run-test-btn" onClick={runTest}>
              Run Analysis
            </button>

            {renderResults()}
          </>
        ) : (
          <div className="select-test-prompt">
            <TrendingUp size={48} />
            <h3>Select a Statistical Test</h3>
            <p>Choose a test category from the sidebar, then select a specific test to run on your survey data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticalTests;
